import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import api from '../../utils/api';

const RecruitmentManagement = ({ onUpdate }) => {
  const [recruitment, setRecruitment] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRec, setEditingRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    benefits: '',
    location: '',
    employment_type: '',
    is_active: true,
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchRecruitment();
  }, []);

  const fetchRecruitment = async () => {
    try {
      const response = await api.get('/recruitment');
      setRecruitment(response.data);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('採用情報の取得に失敗しました:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingRec) {
        await api.put(`/recruitment/${editingRec.id}`, formData);
        toast({
          title: '更新完了',
          description: '採用情報を更新しました',
        });
      } else {
        await api.post('/recruitment', formData);
        toast({
          title: '作成完了',
          description: '新しい採用情報を作成しました',
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchRecruitment();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '操作に失敗しました',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingRec(item);
    setFormData({
      title: item.title,
      description: item.description,
      requirements: item.requirements,
      benefits: item.benefits,
      location: item.location,
      employment_type: item.employment_type,
      is_active: item.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) {
      return;
    }

    try {
      await api.delete(`/recruitment/${id}`);
      toast({
        title: '削除完了',
        description: '採用情報を削除しました',
      });
      fetchRecruitment();
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.response?.data?.detail || '削除に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      requirements: '',
      benefits: '',
      location: '',
      employment_type: '',
      is_active: true,
    });
    setEditingRec(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  return (
    <Card className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border-amber-500/30 backdrop-blur-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <CardTitle className="text-xl sm:text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
            採用情報管理
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            {!isEditMode && (
              <Button 
                onClick={() => setIsEditMode(true)}
                variant="outline"
                className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
              >
                <Edit className="w-4 h-4 mr-2" />
                編集モード
              </Button>
            )}
            {isEditMode && (
              <Button 
                onClick={() => setIsEditMode(false)}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <X className="w-4 h-4 mr-2" />
                編集終了
              </Button>
            )}
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/30"
              >
                <Plus className="w-4 h-4 mr-2" />
                新規作成
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 border-amber-500/30 text-gray-900">
              <DialogHeader>
                <DialogTitle className="text-2xl bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                  {editingRec ? '採用情報を編集' : '新しい採用情報を作成'}
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  {editingRec ? '採用情報の内容を編集します' : '新しい採用情報を作成します'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-gray-600">募集タイトル</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="例: 職業指導員"
                    className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                    required
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location" className="text-gray-600">勤務地</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="例: 三重県津市"
                      className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="employment_type" className="text-gray-600">雇用形態</Label>
                    <Input
                      id="employment_type"
                      value={formData.employment_type}
                      onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                      placeholder="例: 正社員"
                      className="bg-white/50 border-amber-500/30 text-gray-900 focus:border-amber-500"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-gray-600">募集内容</Label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(html) => setFormData({ ...formData, description: html })}
                    placeholder="仕事内容や募集の概要を入力してください"
                    testId="recruit-description-editor"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="requirements" className="text-gray-600">応募資格</Label>
                  <RichTextEditor
                    value={formData.requirements}
                    onChange={(html) => setFormData({ ...formData, requirements: html })}
                    placeholder="応募に必要な資格・条件を入力してください（箇条書き可）"
                    testId="recruit-requirements-editor"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="benefits" className="text-gray-600">待遇・福利厚生</Label>
                  <RichTextEditor
                    value={formData.benefits}
                    onChange={(html) => setFormData({ ...formData, benefits: html })}
                    placeholder="給与・休日・福利厚生などを入力してください（箇条書き可）"
                    testId="recruit-benefits-editor"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active" className="text-gray-600">公開する</Label>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleDialogClose}
                    className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                  >
                    キャンセル
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                    disabled={loading}
                  >
                    {loading ? '処理中...' : (editingRec ? '更新' : '作成')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {recruitment.map((item) => (
            <div
              key={item.id}
              className="border border-amber-500/20 rounded-lg p-4 hover:border-amber-500/50 bg-white/30 transition-all backdrop-blur-sm"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                    <span className={`text-xs px-2 py-1 rounded ${item.is_active ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-100/20 text-gray-600 border border-gray-300/30'}`}>
                      {item.is_active ? '公開中' : '非公開'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{(item.description || '').replace(/<[^>]+>/g, ' ')}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    <span>📍 {item.location}</span>
                    <span>💼 {item.employment_type}</span>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  {isEditMode && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEdit(item)}
                        className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleDelete(item.id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default RecruitmentManagement;
